import { DEBT_TYPE_SETTINGS_ROUTES, OPERATOR_ONLY_SETTINGS_ROUTES } from '@/lib/app-navigation';
import { isNoDebtTypeCohort } from '@/lib/debt-type-cohort';
import { type DomainT, Scope, type ScopeT } from '@/types';

/**
 * The rule-fragment domains each settings page reads and writes. Pages absent here
 * (debt-types, onboarding) edit entity records rather than config.
 */
const SETTINGS_ROUTE_DOMAINS: Record<string, DomainT[]> = {
  '/settings/organisation': ['content'],
  '/settings/clients': ['content', 'ingestion'],
  '/settings/branding': ['theme', 'content'],
  '/settings/customer-display': ['customer_display'],
  '/settings/commission': ['commercials'],
  '/settings/invoicing': ['commercials'],
  '/settings/model-parameters': ['commercials'],
  '/settings/communications': ['communications'],
  '/settings/notifications': ['communications'],
  '/settings/payments': ['journey'],
  '/settings/authentication': ['journey'],
  '/settings/operations': ['journey', 'governance', 'financial'],
  '/settings/accounting': ['financial'],
  '/settings/payouts': ['payouts'],
  '/settings/sftp': ['connections'],
  '/settings/http-destinations': ['connections'],
  '/settings/api-egress': ['api_egress'],
};

/** Editable with a debt type selected, but the save lands on the client (api_egress is client-scope only). */
const CLIENT_SCOPED_SETTINGS_ROUTES = new Set(['/settings/api-egress']);

export function settingsRouteDomains(pathname: string): DomainT[] {
  return SETTINGS_ROUTE_DOMAINS[pathname] ?? [];
}

export type HierarchyScope = Extract<ScopeT, 'operator' | 'client' | 'debt_type'>;

export interface HierarchyLevel {
  scope: HierarchyScope;
  /** Null when nothing is selected at this level. */
  id: string | null;
  name: string | null;
  /** False when this page's settings are owned by a broader scope, so this level can never override them. */
  applies: boolean;
}

interface Named {
  id: string;
}

/**
 * The full operator → client → debt type chain for the selected context, with
 * unselected levels left blank. Mirrors the read-only owner logic in SettingsLayout:
 * operator-only pages ignore narrower scopes, and a debt type only overrides the
 * pages in DEBT_TYPE_SETTINGS_ROUTES. The "Others" cohort never applies because it
 * reads its client's settings.
 */
export function hierarchyLevelsForRoute(
  pathname: string,
  operator: (Named & { name: string }) | null,
  client: (Named & { display_name: string }) | null,
  debtType: (Named & { name: string }) | null
): HierarchyLevel[] {
  const operatorOnly = OPERATOR_ONLY_SETTINGS_ROUTES.has(pathname);
  const debtTypeApplies = !operatorOnly && DEBT_TYPE_SETTINGS_ROUTES.has(pathname) && !CLIENT_SCOPED_SETTINGS_ROUTES.has(pathname);
  return [
    { scope: Scope.OPERATOR, id: operator?.id ?? null, name: operator?.name ?? null, applies: true },
    { scope: Scope.CLIENT, id: client?.id ?? null, name: client?.display_name ?? null, applies: !operatorOnly },
    {
      scope: Scope.DEBT_TYPE,
      id: debtType?.id ?? null,
      name: debtType?.name ?? null,
      applies: debtTypeApplies && !isNoDebtTypeCohort(debtType),
    },
  ];
}

/** The level whose settings the page is editing: the narrowest selected level that applies. */
export function editedLevel(levels: HierarchyLevel[]): HierarchyScope | null {
  return [...levels].reverse().find((l) => l.id && l.applies)?.scope ?? null;
}
